import { z } from 'zod'
import {
  CRM_COMPANY_READ_CONTRACT_VERSION,
  crmCompanyFileSentSummarySchema,
  crmCompanyReadSideEffectsSchema,
  crmCompanySummarySchema,
  crmCompanyTimelineEntrySchema,
} from './crm-company-read'

/**
 * Bounded CRM company write contracts. Request schemas deliberately preserve the
 * handwritten handlers' permissive transport boundary; domain validation and
 * validation precedence remain in those handlers so existing 400s do not move.
 */
const optionalUnknown = z.unknown().optional()

export const crmCompanyCreateRequestSchema = z.object({
  name: optionalUnknown,
  companyName: optionalUnknown,
  company_name: optionalUnknown,
  domain: optionalUnknown,
  segment: optionalUnknown,
  tier: optionalUnknown,
  country: optionalUnknown,
  notes: optionalUnknown,
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmCompanyPatchRequestSchema = z.object({
  name: optionalUnknown,
  companyName: optionalUnknown,
  company_name: optionalUnknown,
  domain: optionalUnknown,
  segment: optionalUnknown,
  tier: optionalUnknown,
  country: optionalUnknown,
  notes: optionalUnknown,
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  active: optionalUnknown,
}).passthrough()

export const crmCompanyLinkOrgRequestSchema = z.object({
  platformOrgId: optionalUnknown,
  platform_org_id: optionalUnknown,
}).passthrough()

export const crmCompanyInteractionCreateRequestSchema = z.object({
  kind: optionalUnknown,
  interactionKind: optionalUnknown,
  interaction_kind: optionalUnknown,
  direction: optionalUnknown,
  occurredAt: optionalUnknown,
  occurred_at: optionalUnknown,
  summary: optionalUnknown,
  body: optionalUnknown,
  source: optionalUnknown,
  sourceRef: optionalUnknown,
  source_ref: optionalUnknown,
  payloadRef: optionalUnknown,
  payload_ref: optionalUnknown,
  contactId: optionalUnknown,
  contact_id: optionalUnknown,
  leadId: optionalUnknown,
  lead_id: optionalUnknown,
  opportunityId: optionalUnknown,
  opportunity_id: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmCompanyFileSendRequestSchema = z.object({
  uploadId: optionalUnknown,
  upload_id: optionalUnknown,
  contactId: optionalUnknown,
  contact_id: optionalUnknown,
  opportunityId: optionalUnknown,
  opportunity_id: optionalUnknown,
  kind: optionalUnknown,
  sentVia: optionalUnknown,
  sent_via: optionalUnknown,
  label: optionalUnknown,
  sentAt: optionalUnknown,
  sent_at: optionalUnknown,
  supersedesId: optionalUnknown,
  supersedes_id: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

/** The projection action ignores its request body, including an absent body. */
export const crmCompanyPlatformEventsRequestSchema = z.object({}).passthrough()

export const crmCompanyWriteResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  company: crmCompanySummarySchema,
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmCompanyCreateResponseSchema = crmCompanyWriteResponseSchema.extend({
  replayed: z.boolean(),
})

export const crmCompanyInteractionResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  interaction: crmCompanyTimelineEntrySchema,
  replayed: z.boolean(),
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmCompanyFileSendResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  file: crmCompanyFileSentSummarySchema,
  replayed: z.boolean(),
  superseded: z.boolean(),
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmCompanyPlatformEventsResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  ok: z.literal(true),
  projected: z.number().int().nonnegative(),
  platformOrgId: z.string().min(1),
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const CRM_COMPANY_WRITE_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_EVENTS_PROJECTION_NOT_CONFIGURED',
  'CR_IDEMPOTENCY_KEY_REUSED',
] as const

export const crmCompanyWriteErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.enum(CRM_COMPANY_WRITE_ERROR_CODES),
    message: z.string(),
  }).passthrough(),
  reference: z.string().optional(),
}).passthrough()

export type CrmCompanyCreateRequest = z.infer<typeof crmCompanyCreateRequestSchema>
export type CrmCompanyPatchRequest = z.infer<typeof crmCompanyPatchRequestSchema>
export type CrmCompanyLinkOrgRequest = z.infer<typeof crmCompanyLinkOrgRequestSchema>
export type CrmCompanyInteractionCreateRequest = z.infer<typeof crmCompanyInteractionCreateRequestSchema>
export type CrmCompanyFileSendRequest = z.infer<typeof crmCompanyFileSendRequestSchema>
export type CrmCompanyPlatformEventsRequest = z.infer<typeof crmCompanyPlatformEventsRequestSchema>
export type CrmCompanyWriteResponse = z.infer<typeof crmCompanyWriteResponseSchema>
export type CrmCompanyCreateResponse = z.infer<typeof crmCompanyCreateResponseSchema>
export type CrmCompanyInteractionResponse = z.infer<typeof crmCompanyInteractionResponseSchema>
export type CrmCompanyFileSendResponse = z.infer<typeof crmCompanyFileSendResponseSchema>
export type CrmCompanyPlatformEventsResponse = z.infer<typeof crmCompanyPlatformEventsResponseSchema>
export type CrmCompanyWriteErrorCode = (typeof CRM_COMPANY_WRITE_ERROR_CODES)[number]
export type CrmCompanyWriteErrorEnvelope = z.infer<typeof crmCompanyWriteErrorEnvelopeSchema>
